import { ComponentConfig } from '@measured/puck';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../../services/constants';
import { IAddressOptions } from '../../../interface/formInterface';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
} from '../../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../../puck/reusable-props/Border';
import CustomTooltip from '../../../../components/common/tooltip';
import FirstLastNameHelper from './FirstLastNameHelper';
import { defaultBorderProps } from '../../../utils/constants';
import {
  getBorderProps,
  getcurrentDeviceType,
  getNewMarginStyles,
  getPaddingStyles,
  restrictEmojis,
} from '../../../utils/helper';
import {
  marginFields,
  marginProps,
  MarginProps,
} from '../../../puck/reusable-props/NewMargin';
import { TextFieldControl } from '../../form-fields/TextField';
import {
  fieldHeightProps,
  inputFieldHeightProps,
} from '../../../puck/reusable-props/inputfield-height';
import { outlineProps } from '../../../puck/reusable-props/Outline';

export interface IFirstLastNameOption {
  FirstName: IAddressOptions;
  LastName: IAddressOptions;
}

export type FirstLastNameProps = {
  options: IFirstLastNameOption;
  border: IBorderValueProps;
} & MarginProps &
  PaddingProps &
  fieldHeightProps;

export const FirstNameLastName = ({
  useAppSelector,
}: ReduxDispatchProps &
  ReduxSelectorProps): ComponentConfig<FirstLastNameProps> => {
  return {
    label: (
      <CustomTooltip title="First and Last Name">
        <span>First and Last Name</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      options: {
        type: 'custom',
        label: 'Options',
        render: ({ value, onChange }) => {
          return (
            <FirstLastNameHelper
              value={value}
              onChange={onChange}
              name={`First and Last Name`}
            />
          );
        },
      },
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      ...inputFieldHeightProps(false, true),
    },
    defaultProps: {
      ...marginProps,
      ...paddingProps,
      ...borderProps,
      options: {
        FirstName: {
          header: {
            hide: false,
            text: 'First Name',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'First Name',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          disableRequiredValidation: true,
          requiredValidation: true,
          validationText: 'First Name is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        LastName: {
          header: {
            hide: false,
            text: 'Last Name',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Last Name',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          disableRequiredValidation: true,
          validationText: 'Last Name is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
      },
      heightField: {
        height: {
          desktop: '42',
          tablet: '42',
          mobile: '42',
        },
        rowGap: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
        colGap: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
        layout: {
          mobile: 'row',
          tablet: 'row',
          desktop: 'row',
        },
        deviceType: 'desktop',
      },
    },
    render: ({ margin, editMode, padding, border, options, heightField }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);

      const marginStyles = getNewMarginStyles(margin, currentDeviceType);
      const rowGap = heightField?.rowGap?.[currentDeviceType] || '10';
      const colGap = heightField?.colGap?.[currentDeviceType] || '10';

      return (
        <div
          className={`grid  ${heightField?.layout?.[currentDeviceType] === 'row' ? 'grid-cols-2' : 'grid-cols-1'} font-light width-auto-form `}
          style={{
            ...marginStyles,
            ...getPaddingStyles(padding, currentDeviceType),
            ...getBorderProps(border, currentDeviceType),
            gridRowGap: isNaN(Number(rowGap)) ? rowGap : Number(rowGap),
            gridColumnGap: isNaN(Number(colGap)) ? colGap : Number(colGap),
          }}>
          <div className="firstName sm:col-span-1">
            <TextFieldControl
              id={`Shipping.FirstName`}
              name={`Shipping.FirstName`}
              uniqueName={'FirstLastNameShippingFirstName'}
              onChangeHandler={restrictEmojis}
              placeholder={options?.FirstName?.placeholder}
              radiusBorder={options?.FirstName?.border}
              outline={options?.FirstName?.outline}
              header={options?.FirstName?.header}
              autoComplete="given-name"
              fieldHeight={heightField?.height?.[currentDeviceType]}
              placeHolderColor={options?.FirstName?.color}
              textColor={options?.FirstName?.textColor}
              backgroundColor={options?.FirstName?.textBackgroundColor}
              placeHolderFontSize={options?.FirstName?.fontSize}
              placeHolderLineHeight={options?.FirstName?.lineHeight}
              PlaceHolderTextAlign={options?.FirstName?.textAlign}
              placeHolderweight={options?.FirstName?.fontWeight}
              placeHolderFontFamily={options?.FirstName?.fontFamily}
              placeHolderLetterSpace={options?.FirstName.letterSpace}
              currentDeviceType={currentDeviceType}
              rules={{
                required: options?.FirstName?.requiredValidation
                  ? options?.FirstName?.validationText ||
                    'First name is a required field'
                  : false,
              }}
            />
          </div>
          <div className="lastName sm:col-span-1">
            <TextFieldControl
              id={`Shipping.LastName`}
              name={`Shipping.LastName`}
              uniqueName={'FirstLastNameShippingLastName'}
              onChangeHandler={restrictEmojis}
              placeholder={options?.LastName?.placeholder}
              header={options?.LastName?.header}
              placeHolderColor={options?.LastName?.color}
              textColor={options?.LastName?.textColor}
              backgroundColor={options?.LastName?.textBackgroundColor}
              placeHolderFontSize={options?.LastName?.fontSize}
              placeHolderweight={options?.LastName?.fontWeight}
              radiusBorder={options?.LastName?.border}
              outline={options?.LastName?.outline}
              PlaceHolderTextAlign={options?.LastName?.textAlign}
              placeHolderLineHeight={options?.LastName?.lineHeight}
              placeHolderLetterSpace={options?.LastName.letterSpace}
              placeHolderFontFamily={options?.LastName?.fontFamily}
              currentDeviceType={currentDeviceType}
              autoComplete="family-name"
              fieldHeight={heightField?.height?.[currentDeviceType]}
              rules={{
                required: options?.LastName?.requiredValidation
                  ? options?.LastName?.validationText ||
                    'Last name is a required field'
                  : false,
              }}
            />
          </div>
        </div>
      );
    },
  };
};
